import React, {
  memo,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useKeydown } from '../../hooks/useKeydown';
import { GridViewProps, ItemProps } from './GridView.types';

export const GridView: React.FC<GridViewProps> = memo(
  ({
    id,
    uniqueKey = 'grid-view-',
    nativeControl = false,
    scrollOffset = 0,
    rowItemsCount = 5,
    rowCount,
    bufferStart = 0,
    bufferEnd = 0,
    itemWidth,
    itemHeight,
    isActive,
    initialActiveIndex = 0,
    direction = 'ltr',
    debounce = 200,
    onMouseEnter,
    onChangeRow,
    onChangeCol,
    onUp,
    onDown,
    onLeft,
    onRight,
    onOk,
    onBack,
    renderItem,
    data = [],
    gap = 1,
    rowGap = gap,
  }) => {
    const scrollViewRef = useRef<HTMLDivElement>(null);
    const [startRow, setStartRow] = useState(0);
    const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
    const containerRef = useRef<HTMLDivElement>(null);

    // Memoized dimensions calculation
    const dimensions = useMemo(() => {
      const DEFAULT_ROW_ITEMS = 5;
      const DEFAULT_ITEM_WIDTH = 25;
      const DEFAULT_ITEM_HEIGHT = 25;

      const calculatedRowItems = rowItemsCount || DEFAULT_ROW_ITEMS;
      const calculatedRows =
        rowCount || Math.ceil(data.length / calculatedRowItems) || 1;

      return {
        itemWidth: itemWidth || DEFAULT_ITEM_WIDTH,
        itemHeight: itemHeight || DEFAULT_ITEM_HEIGHT,
        rowItems: calculatedRowItems,
        rows: calculatedRows,
      };
    }, [itemWidth, itemHeight, rowItemsCount, rowCount, data.length]);

    const changeStartRow = useCallback(
      (index: number) => {
        const startScrollRow = 2;
        const currentRow = Math.ceil((index + 1) / dimensions.rowItems);
        let row = currentRow - startScrollRow;
        if (row < 0) row = 0;
        setStartRow(row);
      },
      [dimensions.rowItems]
    );

    useEffect(() => {
      setActiveIndex(initialActiveIndex);
      changeStartRow(initialActiveIndex);
    }, [id, initialActiveIndex, changeStartRow]);

    const left = useCallback(() => {
      setActiveIndex((prev) => {
        if (prev % dimensions.rowItems === 0) {
          onLeft?.(prev);
          return prev;
        }

        const newIndex = prev - 1;
        const colIndex = newIndex % dimensions.rowItems;
        onChangeCol?.(colIndex);
        return newIndex;
      });
    }, [dimensions.rowItems, onLeft, onChangeCol]);

    const right = useCallback(() => {
      setActiveIndex((prev) => {
        if (
          prev % dimensions.rowItems === dimensions.rowItems - 1 ||
          prev === data.length - 1
        ) {
          onRight?.(prev);
          return prev;
        }

        const newIndex = prev + 1;
        const colIndex = newIndex % dimensions.rowItems;
        onChangeCol?.(colIndex);
        return newIndex;
      });
    }, [dimensions.rowItems, data.length, onRight, onChangeCol]);

    const up = useCallback(() => {
      setActiveIndex((prev) => {
        if (prev < dimensions.rowItems) {
          onUp?.(prev);
          return prev;
        }

        const newIndex = prev - dimensions.rowItems;
        const rowIndex = Math.floor(newIndex / dimensions.rowItems);
        onChangeRow?.(rowIndex);
        changeStartRow(newIndex);
        return newIndex;
      });
    }, [dimensions.rowItems, onUp, onChangeRow, changeStartRow]);

    const down = useCallback(() => {
      setActiveIndex((prev) => {
        const currentRow = Math.ceil((prev + 1) / dimensions.rowItems);
        const totalRows = Math.ceil(data.length / dimensions.rowItems);

        if (currentRow === totalRows) {
          onDown?.(prev);
          return prev;
        }

        let newIndex = prev + dimensions.rowItems;
        if (newIndex > data.length - 1) {
          newIndex = data.length - 1;
        }

        const rowIndex = Math.floor(newIndex / dimensions.rowItems);
        onChangeRow?.(rowIndex);
        changeStartRow(newIndex);
        return newIndex;
      });
    }, [dimensions.rowItems, data.length, onDown, onChangeRow, changeStartRow]);

    const ok = useCallback(() => {
      if (data.length > 0 && activeIndex >= 0 && activeIndex < data.length) {
        onOk?.(data[activeIndex], activeIndex);
      }
    }, [onOk, data, activeIndex]);

    const back = useCallback(() => {
      setActiveIndex(0);
      changeStartRow(0);
      onBack?.(0);
    }, [onBack, changeStartRow]);

    const onMouseEnterItem = useCallback(
      (index: number) => {
        setActiveIndex(index);
        onMouseEnter?.(index);
      },
      [onMouseEnter]
    );

    const getItemStyle = useCallback(
      (index: number): React.CSSProperties => {
        const vIndex = Math.floor(index / dimensions.rowItems);
        const hIndex = index % dimensions.rowItems;

        return {
          position: 'absolute',
          width: `${dimensions.itemWidth}rem`,
          height: `${dimensions.itemHeight}rem`,
          top: `${vIndex * (dimensions.itemHeight + rowGap)}rem`,
          [direction === 'rtl' ? 'right' : 'left']: `${
            hIndex * (dimensions.itemWidth + gap)
          }rem`,
        };
      },
      [dimensions, gap, rowGap, direction]
    );

    const renderedItems = useMemo(() => {
      const items: React.ReactNode[] = [];

      if (!dimensions.rowItems || data.length === 0) {
        return items;
      }

      const visibleStart = Math.max(
        0,
        startRow * dimensions.rowItems - dimensions.rowItems * bufferStart
      );

      const visibleEnd = Math.min(
        data.length,
        startRow * dimensions.rowItems +
          dimensions.rowItems * dimensions.rows +
          dimensions.rowItems * bufferEnd
      );

      for (let i = visibleStart; i < visibleEnd; i++) {
        const itemProps: ItemProps = {
          key: `${uniqueKey}${i}`,
          index: i,
          style: getItemStyle(i),
          isActive: i === activeIndex && isActive,
          item: data[i],
          onMouseEnter: () => onMouseEnterItem(i),
        };
        items.push(renderItem(itemProps));
      }
      return items;
    }, [
      startRow,
      bufferStart,
      bufferEnd,
      uniqueKey,
      dimensions,
      getItemStyle,
      activeIndex,
      isActive,
      data,
      onMouseEnterItem,
      renderItem,
    ]);

    useEffect(() => {
      const applyTransform = () => {
        if (!scrollViewRef.current) return;

        let offset = startRow * (dimensions.itemHeight + rowGap);
        const currentRow = Math.ceil((activeIndex + 1) / dimensions.rowItems);

        // Apply scroll offset consistently for all rows after the first
        if (currentRow > 1) {
          offset += scrollOffset;
        }

        const transform = `translate3d(0, -${offset}rem, 0)`;
        scrollViewRef.current.style.transform = transform;
        scrollViewRef.current.style.webkitTransform = transform;
      };

      applyTransform();
    }, [
      startRow,
      dimensions.itemHeight,
      dimensions.rowItems,
      scrollOffset,
      rowGap,
      activeIndex,
    ]);

    const keyDownOptions = useMemo(
      () => ({
        isActive: isActive && nativeControl,
        debounce,
        left,
        right,
        up,
        down,
        back,
        ok,
      }),
      [isActive, nativeControl, left, right, up, down, back, ok, debounce]
    );

    useKeydown(keyDownOptions);

    useEffect(() => {
      setStartRow(0);
      setActiveIndex(0);
    }, [data]);

    return (
      <div
        ref={containerRef}
        className="scroll-view-parent"
        style={{ width: '100%', height: '100%', position: 'relative' }}
      >
        <div className="scroll-view grid-view" ref={scrollViewRef}>
          {renderedItems}
        </div>
      </div>
    );
  }
);

GridView.displayName = 'GridView';
